import { mapState, mapGetters, mapActions } from 'vuex';
import { format, parseISO, isAfter } from 'date-fns';
import { axiosBlobClient } from 'utils/download';
import { formatByteSize, sortArray, cloneObject } from 'utils';
import { saveAs } from 'file-saver';

export default {
  props: {
    asset: {
      type: Object,
      required: true
    },
    localeLevel: {
      type: Boolean,
      default: false
    },
    product: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      downloadRunning: false,
      historyVisible: false
    };
  },
  computed: {
    ...mapState(['fieldManifest', 'basket', 'favorites']),
    ...mapState('generic', ['services', 'industries']),
    ...mapState('user', ['activeUser']),
    ...mapGetters('user', ['isInternal']),
    ...mapGetters(['isMySheets']),
    typeLabel() {
      if (this.localeLevel) {
        const sameType = this.product.assets.filter((a) => a.type === this.asset.type);

        if (sameType.filter((a) => a.locale !== this.asset.locale).length > 0) {
          return this.asset.locale;
        }

        return null;
      }

      return this.fieldManifest.hasOwnProperty(this.asset.type)
        ? this.fieldManifest[this.asset.type].title
        : this.asset.type;
    },
    created() {
      return this.asset.createdDate ? format(parseISO(this.asset.createdDate), 'yyyy/MM/dd') : 'n/a';
    },
    version() {
      return this.asset.version ?? 'n/a';
    },
    isInBasket() {
      return !!this.basket.find((asset) => asset.id === this.asset.id);
    },
    productFavorite() {
      return this.favorites.find((s) => s.productId === this.product.prd);
    },
    isFavored() {
      // Check if there is any favorite for that prd
      if (this.productFavorite) {
        // No document types defined, so the whole pigment is favored
        if (!this.productFavorite.documentTypes) return true;

        if (Array.isArray(this.productFavorite.documentTypes)) {
          // No document types defined, so the whole pigment is favored
          if (this.productFavorite.documentTypes.length === 0) return true;

          // Limited to some document types, check if the current asset type is included
          return this.productFavorite.documentTypes.indexOf(this.asset.type) > -1;
        }
      }

      return false;
    },
    favoriteStateIcon() {
      return this.isFavored ? ['fas', 'star'] : ['far', 'star'];
    },
    isNew() {
      if (this.activeUser && this.activeUser.previousLogin) {
        return isAfter(parseISO(this.asset.createdDate), parseISO(this.activeUser.previousLogin));
      }

      return false;
    },
    isNewDocument() {
      return this.isNew && this.asset.isRootVersion;
    },
    isNewVersion() {
      return this.isNew && !this.asset.isRootVersion;
    },
    niceFileSize() {
      return formatByteSize(this.asset.size, ' ');
    },
    showHistoryAction() {
      return this.isInternal && !this.asset.isRootVersion;
    },
    industriesForTracking() {
      if (!this.asset.industries || this.industries.length === this.asset.industries.length) {
        return 'all_industries';
      }

      return this.industries.filter((ind) => this.asset.industries.includes(ind.code)).map((ind) => ind.title).join(',');
    },
    showDirectDownloadLinkCta() {
      return this.isInternal;
    },
    directDownloadLink() {
      return `${this.services.backend.url}/util/download/document/${this.asset.id}`;
    },
    manifestForAssetType() {
      return this.fieldManifest.hasOwnProperty(this.asset.type)
        ? this.fieldManifest[this.asset.type]
        : null;
    },
    accessLevelsForAsset() {
      if (this.manifestForAssetType) {
        const accessLevels = cloneObject(this.manifestForAssetType.accessLevel);

        return accessLevels.filter((acl) => this.asset.industries.includes(acl.industry));
      }

      return null;
    },
    mostRestrictiveAccessLevel() {
      const accessLevels = cloneObject(this.accessLevelsForAsset);

      if (accessLevels) {
        sortArray(accessLevels, 'weight', 'asc');

        return accessLevels.shift();
      }

      return null;
    }
  },
  methods: {
    ...mapActions(['saveFavorite', 'deleteFavorite']),
    async downloadAsset() {
      this.downloadRunning = true;

      let url = `${this.services.documentservice.url}/api/v2/document/download/${this.asset.id}`;
      if (this.activeUser) url += `?jwt=${this.activeUser.token}`;

      try {
        const response = await axiosBlobClient.get(url);
        saveAs(new Blob([response.data]), this.asset.filename);
        this.downloadRunning = false;

        this.$trackEvent(
          'Download',
          'Download Product Document',
          {
            assetType: this.asset.type,
            prd: this.product.prd,
            assetIndustries: this.industriesForTracking,
            filename: this.asset.filename
          }
        );
      } catch (e) {
        this.downloadRunning = false;
        this.$notification.error(e.response?.data?.message ?? e.message);
      }
    },
    toggleBasket() {
      if (!this.isInBasket) this.$store.dispatch('addToBasket', [this.asset]);
      else this.$store.dispatch('removeFromBasket', [this.asset]);
    },
    toggleFavorite() {
      if (this.isFavored) this.removeFavorite();
      else this.addFavorite();
    },
    addFavorite() {
      const payload = {
        productId: this.product.prd,
        documentTypes: [this.asset.type]
      };

      // Merge already favored asset types
      if (this.productFavorite && this.productFavorite.documentTypes && Array.isArray(this.productFavorite.documentTypes)) {
        payload.documentTypes = payload.documentTypes.concat(this.productFavorite.documentTypes);
      }

      this.saveFavorite(payload);

      this.$trackEvent(
        'Product Document Download',
        'Add to MySheets',
        {
          assetType: this.asset.type,
          prd: this.product.prd,
          assetIndustries: this.industriesForTracking,
          filename: this.asset.filename
        }
      );
    },
    removeFavorite() {
      let favoriteTypes;

      if (this.productFavorite
                && this.productFavorite.documentTypes
                && Array.isArray(this.productFavorite.documentTypes)
                && this.productFavorite.documentTypes.length > 0) {
        // Is favorite for x document types, remove the current one from the array
        favoriteTypes = this.productFavorite.documentTypes.filter((type) => type !== this.asset.type);
      } else {
        // Is favored on pigment level, get all visible asset types except the current one
        favoriteTypes = this.product.assets.map((a) => a.type).filter((type) => type !== this.asset.type);
      }

      // Delete the favorite if no types are left, update otherwise
      if (favoriteTypes.length === 0) {
        this.deleteFavorite(this.productFavorite.id);
      } else {
        this.saveFavorite({
          productId: this.product.prd,
          documentTypes: favoriteTypes
        });
      }

      this.$trackEvent(
        'Product Document Download',
        'Remove from MySheets',
        {
          assetType: this.asset.type,
          prd: this.product.prd,
          assetIndustries: this.industriesForTracking,
          filename: this.asset.filename
        }
      );
    },
    showHistory() {
      this.historyVisible = true;
    }
  }
};
